import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isAllowEdit, renderCertificateBadge } from 'features/supplier/utils';

import ImageUpload from '@components/ImageUpload';
import ModalContainer from '@components/modals/ModalContainer';
import NoData from '@components/NoData';

import ComplianceGrade from '@features/supplier/component/ComplianceGrade';
import ViewCertificates from '@features/supplier/component/ViewCertificates';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getSuppliersCertificates, getSuppliersDetails } from '@store/reducers';
import { fetchSupplierDetails, fetchSuppliersCertificates } from '@store/supplier/action';

import { fetchEnumValues } from '@services/commonService';
import { onAddCertificate, onUpdateCertificate } from '@services/supplierService';

import { changeDateFormat, checkFileExtention, getFileType, getIconByFileType } from '@utils/Util';

const Certificates = () => {
	const supplierCertificates = useSelector(getSuppliersCertificates);
	const [certificateTypes, setCertificateTypes] = useState([]);
	const [selectedItem, setSelecteItem] = useState(null);
	const [errors, setErrors] = useState({});
	const [minDate] = useState(null);
	const [isAdd, setIsAdd] = useState(false);
	const [isViewCertificates, setIsViewCertificates] = useState(null);
	const [isEdit, setIsEdit] = useState(false);
	const [, setIsShowInfo] = useState(false);
	const isLoading = useSelector((store) => store.loaderStore);
	const supplierDetails = useSelector(getSuppliersDetails);

	const dispatch = useDispatch();
	const { id } = useParams();
	const rulesRef = useRef(null);

	useEffect(() => {
		dispatch(fetchSuppliersCertificates(id));
		onFetchData();
	}, [dispatch, id]);

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, { capture: true });
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (e) => {
		if (rulesRef.current && !rulesRef.current.contains(e.target)) {
			setIsShowInfo(false);
		}
	};

	const onFetchData = async () => {
		let responseData = await fetchEnumValues('info.nitex.app.supplier.CertificateType');
		setCertificateTypes(responseData);
	};

	const renderFileDummyImage = (type) => {
		let fileType;
		if (type.docUrl) {
			fileType = getFileType(type.docUrl);
		} else {
			fileType = getFileType(type.name);
		}
		if (fileType === 'IMAGE' || fileType === 'NO_FILE') return <img src={type.docUrl} alt="Upload" />;
		else
			return (
				<img
					className="cursor-pointer file-icon"
					src={getIconByFileType(fileType)}
					// onClick={() => {
					// 	if (type.docUrl) {
					// 		window.open(type.docUrl, '_blank').focus();
					// 	}
					// }}
					alt="certificate-image"
				/>
			);
	};

	const onUploadImage = (image) => {
		setSelecteItem({ ...selectedItem, documentDTO: image });
	};

	const renderPreview = (type, previewType) => {
		const fileType = checkFileExtention(type);
		if (fileType) {
			return (
				<>
					{renderFileDummyImage(type)}
					<ImageUpload
						size="sm"
						onUploadImage={onUploadImage}
						docType={'SUPPLIER_CERTIFICATE'}
						type={'imageDocument'}
						isEditable={true}
					/>
				</>
			);
		} else
			return (
				<>
					<img className="border-0" src={type.base64Str ? type.base64Str : type.docUrl} alt="" />
					<ImageUpload
						size="sm"
						onUploadImage={onUploadImage}
						docType={previewType === 'certificate' ? 'SUPPLIER_CERTIFICATE' : 'BADGE_DOCUMENT'}
						type={'imageDocument'}
						isEditable={true}
					/>
				</>
			);
	};

	const resetAll = () => {
		setSelecteItem(null);
		setErrors({});
		setIsAdd(false);
		setIsEdit(false);
	};

	const isValidForm = (body) => {
		let newErrors = {};
		if (!body?.certificateType) {
			newErrors = { ...newErrors, certificateTypeError: 'Certificate type is required' };
		}
		if (!body.expireDate) {
			newErrors = { ...newErrors, expireDateError: 'Expiry date is required' };
		}

		if (isAdd && !body.documentDTO) {
			newErrors = { ...newErrors, imageError: 'Photo is required' };
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const addCertificate = async (body) => {
		try {
			dispatch(openLoader());
			const isValid = isValidForm(body);
			if (!isValid) {
				dispatch(closeLoader());
				return;
			}
			await onAddCertificate(id, body);
			dispatch(fetchSupplierDetails(id));
			dispatch(fetchSuppliersCertificates(id));
			dispatch(closeLoader());
			resetAll();
			setIsAdd(false);
		} catch {
			dispatch(closeLoader());
		}
	};

	const updateCertificate = async (body) => {
		try {
			dispatch(openLoader());
			const isValid = isValidForm(body);
			if (!isValid) {
				dispatch(closeLoader());
				return;
			}
			await onUpdateCertificate(selectedItem?.id, body);
			dispatch(fetchSupplierDetails(id));
			dispatch(fetchSuppliersCertificates(id));
			dispatch(closeLoader());
			resetAll();
			setIsEdit(false);
		} catch {
			dispatch(closeLoader());
		}
	};

	const onSubmit = () => {
		let body = {};
		if (selectedItem?.documentDTO?.base64Str || selectedItem?.badgeDocumentDTO?.base64Str) {
			body = { ...selectedItem };
		} else {
			body = {
				certificateType: selectedItem?.certificateType,
				expireDate: selectedItem?.expireDate,
				id: selectedItem?.id
			};
		}
		if (!selectedItem?.documentDTO?.base64Str) {
			body['documentDTO'] = null;
		}

		if (isAdd) {
			addCertificate(body);
		}
		if (isEdit) {
			updateCertificate(body);
		}
	};

	return (
		<div className="chertification-section hover-section section-padding">
			<div className=" d-flex justify-content-between align-items-center">
				<div>
					<div className="suplier-title pb-4   d-flex align-items-center">
						<h3 className="font-weight-bold regular-16 mb-0"> Compliance</h3>
						{supplierDetails?.certificateStrength?.name && (
							<span className="product_grade font-weight-bold ml-12">
								({supplierDetails?.certificateStrength?.name})
							</span>
						)}
						<ComplianceGrade type="CERTIFICATE_STRENGTH" />
						{isAllowEdit() && (
							<span
								className="ml-12 cursor-pointer medium-14 blue text-capitalize"
								onClick={() => setIsAdd(true)}
							>
								Add
							</span>
						)}
					</div>
				</div>{' '}
			</div>

			{supplierCertificates?.length === 0 ? (
				<NoData message="No Certifications added" />
			) : (
				<div className="certification-items">
					{supplierCertificates?.map((item) => (
						<div
							className="certification-box same-padding-box d-flex flex-column justify-content-center"
							key={item?.id}
							onClick={() => setIsViewCertificates(item?.certificateType)}
						>
							<div className="crt-img mb-4">
								<img
									src={renderCertificateBadge(item.certificateType)}
									alt="certificate"
									className="w-28 h-28 xl:w-36 xl:h-36 rounded-full object-contain"
								/>
							</div>
							{isAllowEdit() && (
								<div
									className="hover-icon"
									onClick={(e) => {
										e.stopPropagation();
										setIsEdit(true);
										setSelecteItem(item);
									}}
								>
									<button className="border-0 bg-transparent">
										<img
											width="20px"
											src={`${process.env.PUBLIC_URL}/icons/edit-black-20.svg`}
											alt="pencil"
										/>
									</button>
								</div>
							)}
							<p className="title gray_dark_01 regular-18 weight-500">
								{item?.certificateTypeName}
							</p>
							<p className="regular-14 status red-1 radius">
								Expires: {changeDateFormat(item?.expireDate, '', 'DD MMM, YYYY')}
							</p>
							<div className=" key-tag">
								{item?.isExpired && (
									<span className="regular-12 status red-1 radius mr-2">Expired</span>
								)}
								{item?.sinceDate && (
									<span className="regular-12 status cyan-1 radius">
										Since {changeDateFormat(item?.sinceDate, '', 'YYYY')}
									</span>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{(isAdd || isEdit) && (
				<ModalContainer>
					<div className="common-popup certificate-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Certificates</h3>
							</div>
							<div
								className="close-btn"
								onClick={() => {
									setSelecteItem(null);
									setErrors({});
									setIsAdd(false);
									setIsEdit(false);
								}}
							>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="container px-0">
								<div className="row">
									<div className="col-md-4">
										<div className={`upload-section ${isEdit ? 'disable-upload' : ''} `}>
											{selectedItem?.documentDTO ? (
												renderPreview(selectedItem?.documentDTO, 'certificate')
											) : (
												<ImageUpload
													size="sm"
													onUploadImage={onUploadImage}
													docType={'SUPPLIER_CERTIFICATE'}
													type={'imageDocument'}
													isDisabled={isEdit ? true : false}
												/>
											)}
										</div>
										{errors.imageError && <p className="error">{errors.imageError}</p>}
									</div>

									<div className="col-md-8">
										<div className="form-group ">
											<label htmlFor="certificateType">Certificates name*</label>
											<select
												className="w-100"
												name="certificateType"
												id="certificateType"
												value={selectedItem?.certificateType}
												onChange={(e) =>
													setSelecteItem({
														...selectedItem,
														certificateType: e.target.value
													})
												}
												disabled={isEdit}
											>
												<option value={''}>Select</option>
												{certificateTypes?.map((item, idx) => (
													<option value={item.constant} key={idx}>
														{item.name}
													</option>
												))}
											</select>
											{errors.certificateTypeError && (
												<p className="error">{errors.certificateTypeError}</p>
											)}
										</div>
										<div className="form-group mb-0">
											<label htmlFor="expire-date">Expire date*</label>
											<input
												min={minDate ? minDate.toString() : ''}
												type="date"
												id="expire-date"
												value={selectedItem?.expireDate || ''}
												onChange={(e) =>
													setSelecteItem({
														...selectedItem,
														expireDate: e.target.value
													})
												}
											/>
											{errors.expireDateError && (
												<p className="error">{errors.expireDateError}</p>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32 ml-2"
									onClick={() => resetAll()}
								>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									disabled={isLoading}
									onClick={onSubmit}
								>
									Add
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}

			{isViewCertificates && (
				<ViewCertificates
					ocsType={isViewCertificates}
					onClose={() => setIsViewCertificates(null)}
					onAllRemoved={() => dispatch(fetchSuppliersCertificates(id))}
				/>
			)}
		</div>
	);
};

export default Certificates;
